"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTerminal = void 0;
const react_1 = require("react");
const hooks_1 = require("../../hooks");
const echo_1 = require("./commands/echo");
const types_1 = require("./types");
const useFileSystem_1 = require("./useFileSystem");
const utils_1 = require("./utils");
function defaultBootSequence({ setIsEnabled, }) {
    return __awaiter(this, void 0, void 0, function* () {
        setIsEnabled(true);
    });
}
function useTerminal(config) {
    var _a;
    const { commands: fileSystemCommands, currentDirectory, resolvePath, changeDirectory, rootDirectory, } = (0, useFileSystem_1.useFileSystem)({ rootDirItems: config === null || config === void 0 ? void 0 : config.fileSystem });
    const [isEnabled, setIsEnabled] = (0, react_1.useState)(true);
    const [fullscreenApp, setFullscreenApp] = (0, react_1.useState)(null);
    const terminalRoot = (0, react_1.useRef)(null);
    const terminalInput = (0, react_1.useRef)(null);
    const [sideloadedCommands, setSideloadedCommands] = (0, react_1.useState)([]);
    const [commands, setCommands] = (0, react_1.useState)([]);
    const [commandHistory, setCommandHistory] = (0, hooks_1.usePersistedState)([], 'terminalCommandHistory');
    const [stdout, setStdout] = (0, react_1.useState)((_a = config === null || config === void 0 ? void 0 : config.initialMessages) !== null && _a !== void 0 ? _a : []);
    const focusTerminal = (0, react_1.useCallback)((ignoreSelection = false) => {
        var _a, _b;
        const isTextSelected = ((_a = window.getSelection()) === null || _a === void 0 ? void 0 : _a.type) === 'Range';
        if (ignoreSelection || !isTextSelected) {
            (_b = terminalInput.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, []);
    const scrollToBottom = (0, react_1.useCallback)((delay = 1) => {
        const terminal = terminalRoot.current;
        if (!terminal)
            return;
        setTimeout(() => {
            terminal.scrollTop = terminal.scrollHeight;
        }, delay);
    }, []);
    const allCommands = (0, react_1.useRef)([]);
    allCommands.current = commands;
    const currentDirRef = (0, react_1.useRef)(currentDirectory);
    currentDirRef.current = currentDirectory;
    const rootDirRef = (0, react_1.useRef)(rootDirectory);
    const commandDirRef = (0, react_1.useRef)(currentDirectory);
    const sendToStdout = (0, react_1.useCallback)((message, replaceLastMessage) => {
        setStdout((stdout) => {
            const oldMessages = replaceLastMessage && stdout.length
                ? stdout.filter((m, i) => i !== stdout.length - 1)
                : stdout;
            const terminalMessage = typeof message === 'string'
                ? {
                    message,
                    type: types_1.TerminalMessageType.Message,
                }
                : message;
            terminalMessage.workingDirectory = commandDirRef.current;
            return [...oldMessages, terminalMessage];
        });
        scrollToBottom();
    }, [scrollToBottom]);
    const clearStdout = (0, react_1.useCallback)(() => {
        setStdout([]);
    }, []);
    const runFullscreenApp = (0, react_1.useCallback)((app) => {
        setFullscreenApp(app);
    }, []);
    const closeFullscreenApp = (0, react_1.useCallback)(() => {
        setFullscreenApp(null);
        setTimeout(() => {
            var _a;
            (_a = terminalInput.current) === null || _a === void 0 ? void 0 : _a.focus();
        }, 1);
    }, []);
    const addCommandToHistory = (0, react_1.useCallback)((command) => {
        setCommandHistory((commands) => {
            if (commands[0] === command) {
                return commands;
            }
            return [command, ...commands].slice(0, 100);
        });
    }, [setCommandHistory]);
    const executeCommand = (0, react_1.useCallback)((inputStr, forceDisableEcho) => __awaiter(this, void 0, void 0, function* () {
        addCommandToHistory(inputStr);
        const input = inputStr.split(' ');
        const rawCommand = input.splice(0, 1)[0];
        const args = input;
        const fileSystemCommand = resolvePath(rawCommand);
        if ((0, types_1.isFileSystemDirectory)(fileSystemCommand)) {
            sendToStdout({ message: inputStr, type: types_1.TerminalMessageType.Echo });
            if (fileSystemCommand.isLocked) {
                sendToStdout({ message: 'Access Denied.', type: types_1.TerminalMessageType.Error });
                return;
            }
            changeDirectory(fileSystemCommand);
            return;
        }
        const command = (0, types_1.isTerminalCommand)(fileSystemCommand)
            ? fileSystemCommand
            : commands.find((command) => command.name.toLowerCase() === rawCommand.toLowerCase());
        if (!(forceDisableEcho || (command === null || command === void 0 ? void 0 : command.disableEcho))) {
            sendToStdout({ message: inputStr, type: types_1.TerminalMessageType.Echo });
        }
        if (command) {
            commandDirRef.current = currentDirRef.current;
            yield command.func({
                sendToStdout,
                clearStdout,
                executeCommand: (inputString) => executeCommand(inputString, true),
                commands,
                runFullscreenApp,
                closeFullscreenApp,
                changeDirectory,
                setIsEnabled,
            }, args);
        }
        else {
            sendToStdout({
                message: `Command not found: ${rawCommand}`,
                type: types_1.TerminalMessageType.Error,
            });
        }
    }), [
        changeDirectory,
        resolvePath,
        sendToStdout,
        clearStdout,
        commands,
        runFullscreenApp,
        closeFullscreenApp,
        addCommandToHistory,
    ]);
    const customBootSequence = config === null || config === void 0 ? void 0 : config.bootSequence;
    const bootSequence = (0, react_1.useMemo)(() => customBootSequence !== null && customBootSequence !== void 0 ? customBootSequence : defaultBootSequence, [customBootSequence]);
    const initializeTerminal = (0, react_1.useCallback)(() => __awaiter(this, void 0, void 0, function* () {
        clearStdout();
        setIsEnabled(false);
        const context = {
            sendToStdout,
            clearStdout,
            executeCommand: (inputString) => executeCommand(inputString, true),
            commands,
            runFullscreenApp,
            closeFullscreenApp,
            changeDirectory,
            setIsEnabled,
        };
        yield bootSequence(context);
    }), [
        bootSequence,
        changeDirectory,
        clearStdout,
        closeFullscreenApp,
        commands,
        executeCommand,
        runFullscreenApp,
        sendToStdout,
    ]);
    const { customCommands } = config !== null && config !== void 0 ? config : {};
    (0, react_1.useEffect)(() => {
        const allCommands = [
            {
                name: 'clear',
                func: clearStdout,
            },
            {
                name: 'clearHistory',
                func: () => {
                    setCommandHistory([]);
                },
            },
            echo_1.echoCommand,
            ...fileSystemCommands,
            ...(customCommands !== null && customCommands !== void 0 ? customCommands : []),
            ...sideloadedCommands,
        ];
        const helpCommand = {
            name: 'help',
            func: ({ commands, sendToStdout }) => {
                for (const c of commands) {
                    if (!c.helpText)
                        continue;
                    sendToStdout({ message: c.helpText });
                }
            },
        };
        setCommands([helpCommand, ...allCommands]);
    }, [
        sideloadedCommands,
        clearStdout,
        fileSystemCommands,
        customCommands,
        setCommandHistory,
    ]);
    const scrollHistoryCursor = (0, react_1.useRef)(0);
    const historyOriginalCommand = (0, react_1.useRef)('');
    const resetHistoryState = (0, react_1.useCallback)(() => {
        scrollHistoryCursor.current = 0;
        historyOriginalCommand.current = '';
    }, []);
    const processCommand = (0, react_1.useCallback)(() => {
        const input = terminalInput.current;
        if (!input)
            return;
        const message = input.value;
        if (!message)
            return;
        resetHistoryState();
        executeCommand(message);
        input.value = '';
    }, [executeCommand, resetHistoryState]);
    const scrollThroughHistory = (0, react_1.useCallback)((direction) => {
        const input = terminalInput.current;
        if (!input)
            return;
        const currentValue = input.value;
        if (scrollHistoryCursor.current === 0) {
            historyOriginalCommand.current = currentValue;
        }
        const applicableCommands = commandHistory.filter((c) => c.startsWith(historyOriginalCommand.current));
        const directionMovement = direction === types_1.TerminalHistoryScrollDirection.Up ? 1 : -1;
        scrollHistoryCursor.current = Math.min(Math.max(0, scrollHistoryCursor.current + directionMovement), applicableCommands.length);
        const newCommand = applicableCommands[scrollHistoryCursor.current - 1];
        input.value = newCommand !== null && newCommand !== void 0 ? newCommand : historyOriginalCommand.current;
        input.selectionStart = input.value.length;
        input.selectionEnd = input.value.length;
        setTimeout(() => {
            input.selectionStart = input.value.length;
            input.selectionEnd = input.value.length;
        }, 1);
    }, [commandHistory]);
    const handleInput = (0, react_1.useCallback)((event) => {
        var _a;
        switch (event.key) {
            case 'Enter': {
                processCommand();
                break;
            }
            case 'ArrowUp': {
                scrollThroughHistory(types_1.TerminalHistoryScrollDirection.Up);
                break;
            }
            case 'ArrowDown': {
                scrollThroughHistory(types_1.TerminalHistoryScrollDirection.Down);
                break;
            }
            case 'Tab': {
                event.preventDefault();
                const input = terminalInput.current;
                if (!input)
                    return;
                const commandSegments = input.value.split(' ');
                const isCommandWithAutocomplete = commandSegments[0] === 'cd' || commandSegments[0] === 'less';
                const path = (_a = (isCommandWithAutocomplete ? commandSegments[1] : commandSegments[0])) !== null && _a !== void 0 ? _a : '';
                const [resolved, remainingPath] = (0, utils_1.resolveFilepath)(path, currentDirRef.current, rootDirRef.current, true);
                if (remainingPath === null) {
                    return;
                }
                const searchItems = remainingPath && (0, types_1.isFileSystemDirectory)(resolved)
                    ? resolved.items
                    : isCommandWithAutocomplete
                        ? []
                        : allCommands.current;
                const searchString = remainingPath;
                const applicableCommands = searchItems
                    .map((i) => i.name)
                    .filter((c) => c.startsWith(searchString));
                if (applicableCommands.length === 1) {
                    input.value = `${isCommandWithAutocomplete ? `${commandSegments[0]} ` : ''}${path.substr(0, path.length - searchString.length)}${applicableCommands[0]}`;
                }
                break;
            }
            default: {
                resetHistoryState();
            }
        }
    }, [processCommand, scrollThroughHistory, resetHistoryState]);
    const configPrompt = config === null || config === void 0 ? void 0 : config.prompt;
    const prompt = (0, react_1.useMemo)(() => configPrompt !== null && configPrompt !== void 0 ? configPrompt : '$', [configPrompt]);
    return {
        isEnabled,
        stdout,
        terminalRoot,
        terminalInput,
        handleInput,
        sendToStdout,
        clearStdout,
        focusTerminal,
        executeCommand,
        fullscreenApp,
        currentDirectory,
        commandHistory,
        initializeTerminal,
        setSideloadedCommands,
        prompt,
    };
}
exports.useTerminal = useTerminal;
//# sourceMappingURL=useTerminal.js.map